import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from 'Constants/env';
import { ResultsCollection } from 'Interfaces/axiosResponse';
import { MobXProviderContext } from 'mobx-react';
import { fromPromise } from 'mobx-utils';
import ConversationModel, {
  IConversationModel,
} from 'Models/ConversationModel';
import { useNotificationStore } from 'Modules/notification/store';
import React from 'react';
import { NavigateFunction, useNavigate } from 'react-router';
import { RootStore } from 'Stores/RootStore';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import API from '~/api';
import { ConversationType } from '../../deleteConversation/types';
import { useConversationStore } from '../../store/index.store';
import { fetchPinnedConversations } from '../../store/pinned-conversations/index.requests';
import { CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE } from '../index.constants';
import { invalidateStaleMessageCache } from './index.utils';

export const QUERY_KEY_FETCH_CONVERSATIONS = 'conversation.fetchConversations';
export const QUERY_KEY_FETCH_CONVERSATION = 'conversation.fetchConversation';
const QUERY_KEY_POLL_CONVERSATIONS = 'conversation.pollConversations';
const QUERY_KEY_POLL_CONVERSATION = 'conversation.pollConversation';

const MUTATION_KEY_ARCHIVE_CONVERSATION = 'conversation.archiveConversation';
const MUTATION_KEY_UNARCHIVE_CONVERSATION =
  'conversation.unarchiveConversation';
const MUTATION_KEY_MARK_AS_READ = 'conversation.markAsRead';
const MUTATION_KEY_MARK_AS_UNREAD = 'conversation.markAsUnread';

export const fetchConversations = async (
  rootStore: RootStore | RootStoreProps
): Promise<ConversationModel[]> => {
  const personStore =
    (rootStore as RootStore).personStore ??
    (rootStore as RootStoreProps).person;
  const preferenceStore =
    (rootStore as RootStore).preferenceStore ??
    (rootStore as RootStoreProps).preference;
  const participantStore =
    (rootStore as RootStore).participantStore ??
    (rootStore as RootStoreProps).participant;
  const conversationStore =
    (rootStore as RootStore).conversationStore ??
    (rootStore as RootStoreProps).conversation;
  const uiStore =
    (rootStore as RootStore).uiStore ?? (rootStore as RootStoreProps).ui;

  const {
    setIsLoadingConversationsList,
    lastConversationId,
    appendConversationsList,
    conversationsListFilter,
    setHasAttemptedConversationsFetch,
  } = useConversationStore.getState();

  try {
    await personStore.waitUntilLoggedIn();
    await preferenceStore.getExistingPreferenceData();

    setIsLoadingConversationsList(true);

    let conversationsResponse: AxiosResponse<
      ResultsCollection<IConversationModel>
    >;

    if (conversationsListFilter === 'archived') {
      const { conversationsList } = useConversationStore.getState();

      const CURRENT_PAGE =
        Math.floor(
          conversationsList.length / CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE
        ) + 1;

      conversationsResponse = await API.get<
        ResultsCollection<IConversationModel>
      >(
        API_ENDPOINTS.ConversationsArchived(
          CURRENT_PAGE,
          CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE
        )
      );

      conversationStore.loadConversationsGetSuccess(conversationsResponse);
    } else {
      conversationsResponse = await API.get<
        ResultsCollection<IConversationModel>
      >(
        API_ENDPOINTS.ConversationsQuery({
          Limit: CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE,
          ShowCallMessagesInChat:
            preferenceStore.preferences.showCallMessagesInChat,
          SkipId: lastConversationId,
          ShowOnlyGroupConversations: conversationsListFilter === 'group',
        })
      );
    }

    const hasMoreConversations =
      conversationsResponse.data.results.length ===
      CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE;

    let conversations = conversationsResponse.data.results.map(
      (conversationDto) => {
        const conversation = ConversationModel.FromResponseDto(conversationDto);

        // This is absolutely ridiculous, but we need to update the mobx store for now, and follow its rules
        const conversationPbo = fromPromise.resolve({
          data: conversation,
        } as AxiosResponse<ConversationModel>);

        invalidateStaleMessageCache(conversation, rootStore);

        if (conversation.unreadCount > 0) {
          void uiStore.setConversationAndTotalUnreadCount(
            conversation.id,
            conversation.unreadCount,
            conversationPbo,
            conversation.unreadMentionsCount
          );
        } else {
          void uiStore.setConversationAndTotalUnreadCount(
            conversation.id,
            0,
            conversationPbo,
            0
          );
        }

        if (conversation.grouping === 'OneOnOne') {
          void participantStore.loadConversationParticipantsIfMissing(
            conversation.id
          );
        }

        return conversation;
      }
    );

    if (conversationsListFilter === 'unread') {
      conversations = conversations.filter(
        (conversation) => conversation.unreadCount > 0
      );
    }

    let pinnedConversations: ConversationModel[] | undefined;
    // Only fetch pinned conversations if we're not "loading more".
    if (!lastConversationId && conversationsListFilter !== 'archived') {
      pinnedConversations = await fetchPinnedConversations(rootStore);
    }

    appendConversationsList(
      conversations,
      hasMoreConversations,
      pinnedConversations
    );

    return conversations;
  } catch (error) {
    const { shouldSuppressErrorNotification } = await import(
      '../../../../providers/PollingProvider'
    );

    const shouldSuppress = shouldSuppressErrorNotification();

    if (!shouldSuppress) {
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error loading conversations',
        message: "We couldn't load your conversations. Please try again.",
      });
    }

    throw error;
  } finally {
    setIsLoadingConversationsList(false);
    // This is the least convoluted way to check if the initial conversations fetch has been attempted while we don't move all these requests to react query
    setHasAttemptedConversationsFetch(true);
  }
};

const archiveConversation = async (
  rootStore: RootStoreProps,
  navigate: NavigateFunction,
  conversationId: string,
  source: 'chat_list' | 'conversation' = 'chat_list'
) => {
  const { person: personStore, conversation: conversationStore } = rootStore;

  try {
    await personStore.waitUntilLoggedIn();

    // Remove conversation from pinned conversations if it's pinned
    const { pinnedConversations, removePinnedConversation } =
      useConversationStore.getState();
    const isPinned = pinnedConversations.some(
      (conv) => conv.id === conversationId
    );
    if (isPinned) {
      removePinnedConversation(conversationId);
    }

    const archiveConversationResponse = await API.delete<ConversationModel>(
      API_ENDPOINTS.ConversationRemove(conversationId, 'LastMessage')
    );

    useConversationStore.getState().removeConversationFromList(conversationId);

    await fetchConversation(rootStore, conversationId);

    if (conversationStore.CurrentConversation?.id === conversationId) {
      void navigate('/');
    }

    // Analytics tracking
    const eventName =
      source === 'chat_list'
        ? 'chat_list_archive'
        : 'conversation_more_archive';
    pushToGTMDataLayer(eventName, {
      source,
      conversationId,
      timestamp: new Date().toISOString(),
    });

    // Success notification with undo action
    useNotificationStore.getState().addNotify({
      type: 'success',
      autoDismiss: true,
      dismissible: true,
      title: 'Success',
      message: 'Conversation archived.',
      action: {
        label: 'Undo',
        onClick: () => {
          void unarchiveConversation(
            rootStore,
            navigate,
            conversationId,
            'notification_undo'
          );
        },
      },
    });

    return archiveConversationResponse;
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error archiving conversation',
      message: "We couldn't archive the conversation. Please try again",
    });
  }
};

const unarchiveConversation = async (
  rootStore: RootStoreProps,
  navigate: NavigateFunction,
  conversationId: string,
  source:
    | 'chat_list'
    | 'conversation'
    | 'conversation_detail'
    | 'notification_undo' = 'chat_list'
) => {
  const { person: personStore, conversation: conversationStore } = rootStore;

  try {
    await personStore.waitUntilLoggedIn();

    const unarchiveConversationResponse = await API.post<ConversationModel>(
      API_ENDPOINTS.ConversationUnarchive(conversationId)
    );

    const isConversationOpen =
      conversationStore.CurrentConversation?.id === conversationId;

    // Note 3: When unarchiving, remove from archived list if we're in archived filter
    const currentFilter =
      useConversationStore.getState().conversationsListFilter;
    if (currentFilter === 'archived') {
      useConversationStore
        .getState()
        .removeConversationFromList(conversationId);
    }
    if (isConversationOpen) {
      void navigate('/?filter=all');
    }
    const filterAfterUnarchive =
      useConversationStore.getState().conversationsListFilter;

    if (filterAfterUnarchive === 'archived') {
      useConversationStore.getState().setConversationsListFilter('all');
    } else if (filterAfterUnarchive === 'all') {
      useConversationStore.getState().setConversationsListFilter('unread');
      useConversationStore.getState().setConversationsListFilter('all');
    }

    await fetchConversation(rootStore, conversationId);
    await fetchConversations(rootStore);

    // Analytics tracking
    let eventName = 'conversation_unarchive';
    if (source === 'conversation_detail') {
      eventName = 'conversation_detail_unarchive';
    } else if (source === 'notification_undo') {
      eventName = 'notification_undo_archive';
    }

    pushToGTMDataLayer(eventName, {
      source,
      conversationId,
      timestamp: new Date().toISOString(),
    });

    // Success notification with undo action (to re-archive)
    useNotificationStore.getState().addNotify({
      type: 'success',
      autoDismiss: true,
      dismissible: true,
      title: 'Success',
      message: 'Conversation unarchived.',
      action: {
        label: 'Undo',
        onClick: () => {
          void archiveConversation(
            rootStore,
            navigate,
            conversationId,
            'chat_list'
          );
        },
      },
    });

    return unarchiveConversationResponse;
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error unarchiving conversation',
      message: "We couldn't unarchive the conversation. Please try again",
    });
  }
};

export const pollConversations = async (
  rootStore: RootStoreProps
): Promise<ConversationModel[]> => {
  const {
    person: personStore,
    preference: preferenceStore,
    participant: participantStore,
    conversation: conversationStore,
  } = rootStore;

  const { mergeConversationsListFromPolling, conversationsListFilter } =
    useConversationStore.getState();

  try {
    await personStore.waitUntilLoggedIn();
    await preferenceStore.getExistingPreferenceData();

    const responses = await Promise.all([
      (async () => {
        const conversationsResponse = await API.get<
          ResultsCollection<IConversationModel>
        >(
          API_ENDPOINTS.ConversationsQuery({
            Limit: CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE,
            ShowCallMessagesInChat:
              preferenceStore.preferences.showCallMessagesInChat,
          })
        );

        conversationStore.loadConversationsGetSuccess(conversationsResponse);

        return conversationsResponse;
      })(),
      fetchPinnedConversations(rootStore),
    ]);

    let conversationsResponse = responses[0];
    const pinnedConversations = responses[1];

    /**
     * If we're filtering by group, we only want to poll the group conversations.
     * But at the same time, we need to get all the conversations to update the unread counts.
     * So we need to get all the conversations anyway, but then fetch only the group conversations to display in the list.
     */
    if (conversationsListFilter === 'group') {
      conversationsResponse = await API.get<
        ResultsCollection<IConversationModel>
      >(
        API_ENDPOINTS.ConversationsQuery({
          Limit: CONVERSATIONS_LIST_CONVERSATIONS_PER_PAGE,
          ShowCallMessagesInChat:
            preferenceStore.preferences.showCallMessagesInChat,
          ShowOnlyGroupConversations: true,
        })
      );
    }

    let conversations = conversationsResponse.data.results.map(
      (conversationDto) => {
        const conversation = ConversationModel.FromResponseDto(conversationDto);

        invalidateStaleMessageCache(conversation, rootStore);

        if (conversation.grouping === 'OneOnOne') {
          void participantStore.loadConversationParticipantsIfMissing(
            conversation.id
          );
        }

        return conversation;
      }
    );

    if (conversationsListFilter === 'unread') {
      conversations = conversations.filter(
        (conversation) => conversation.unreadCount > 0
      );
    }

    if (conversationsListFilter === 'archived') {
      return conversations;
    }

    mergeConversationsListFromPolling(conversations, pinnedConversations);
  } catch (error) {
    console.error('Error polling conversations:', error);
  }
};

export const fetchConversation = async (
  rootStore: RootStore | RootStoreProps,
  conversationId: string
): Promise<ConversationModel> => {
  const conversationStore =
    (rootStore as RootStore).conversationStore ??
    (rootStore as RootStoreProps).conversation;

  const conversationDto = await API.get<IConversationModel>(
    API_ENDPOINTS.ConversationById(conversationId)
  );

  conversationStore.loadSingleConversationSuccess(conversationDto);

  const conversation = ConversationModel.FromResponseDto(conversationDto.data);

  useConversationStore.getState().updateConversation(conversation);

  return conversation;
};

export const useFetchConversations = () => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  const { conversationsListFilter } = useConversationStore();

  return useQuery({
    queryKey: [QUERY_KEY_FETCH_CONVERSATIONS, conversationsListFilter],
    queryFn: async () => {
      const conversations = await fetchConversations(rootStore);

      return conversations;
    },
    enabled: false,
    staleTime: 0,
  });
};

export const useArchiveConversation = () => {
  const navigate = useNavigate();
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  return useMutation({
    mutationKey: [MUTATION_KEY_ARCHIVE_CONVERSATION],
    mutationFn: async ({
      conversationId,
      source,
    }: {
      conversationId: string;
      source?: 'chat_list' | 'conversation';
    }) => {
      const archivedConversation = await archiveConversation(
        rootStore,
        navigate,
        conversationId,
        source
      );

      return archivedConversation;
    },
  });
};

export const useUnarchiveConversation = () => {
  const navigate = useNavigate();
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  return useMutation({
    mutationKey: [MUTATION_KEY_UNARCHIVE_CONVERSATION],
    mutationFn: async ({
      conversationId,
      source,
    }: {
      conversationId: string;
      source?:
        | 'chat_list'
        | 'conversation'
        | 'conversation_detail'
        | 'notification_undo';
    }) => {
      const unarchivedConversation = await unarchiveConversation(
        rootStore,
        navigate,
        conversationId,
        source
      );

      const currentFilter =
        useConversationStore.getState().conversationsListFilter;

      // If we're in the archived filter, switch to "all" to show the unarchived conversation
      if (currentFilter === 'archived') {
        useConversationStore.getState().setConversationsListFilter('all');
      } else {
        const tempFilter = currentFilter === 'all' ? 'unread' : 'all';
        useConversationStore.getState().setConversationsListFilter(tempFilter);
        useConversationStore
          .getState()
          .setConversationsListFilter(currentFilter);
      }

      await fetchConversations(rootStore);

      return unarchivedConversation;
    },
  });
};

export const usePollConversations = (
  enabled: boolean,
  refetchInterval: number | (() => number)
) => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  const { hasAttemptedConversationsFetch } = useConversationStore();
  // Only start polling after the initial conversations fetch has been attempted
  // (either succeeded or failed). This guarantees the poller never becomes the
  // first request and still kicks in even if the initial fetch fails.
  const hasAttemptedInitialFetch = Boolean(hasAttemptedConversationsFetch);

  return useQuery({
    queryKey: [QUERY_KEY_POLL_CONVERSATIONS],
    queryFn: async () => {
      const conversations = await pollConversations(rootStore);

      return conversations;
    },
    enabled: enabled && hasAttemptedInitialFetch,
    refetchInterval,
    staleTime: 0,
  });
};

export const usePollConversation = (
  enabled: boolean,
  conversationId?: string
) => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  return useQuery({
    queryKey: [QUERY_KEY_POLL_CONVERSATION, enabled, conversationId],
    queryFn: async () => {
      if (!conversationId) {
        return null;
      }

      const conversation = await fetchConversation(rootStore, conversationId);

      return conversation;
    },
    enabled: enabled && !!conversationId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const deleteConversation = async (
  rootStore: RootStore | RootStoreProps,
  navigate: NavigateFunction,
  conversationId: string,
  conversationType: ConversationType,
  isActiveGroup = false,
  previousNotificationId?: string | number // Add parameter to track previous notification
) => {
  const {
    person: personStore,
    conversation: conversationStore,
    notification: notificationStore,
    ui: uiStore,
  } = rootStore as RootStoreProps;

  conversationStore.markConversationAsDeleting(conversationId);
  notificationStore?.closeAllMessageCreatedWebNotifications(conversationId);

  try {
    // Remove previous error notification if it exists
    if (previousNotificationId) {
      useNotificationStore
        .getState()
        .removeNotify('tr', previousNotificationId);
    }

    await personStore.waitUntilLoggedIn();

    const deleteConversationResponse = await API.delete<ConversationModel>(
      API_ENDPOINTS.ConversationDelete(conversationId)
    );

    // Remove conversation from local list
    useConversationStore.getState().removeConversationFromList(conversationId);
    // Remove conversation data from MobX stores
    conversationStore.removeLocalConversationData(conversationId);
    conversationStore.markConversationAsRecentlyDeleted(conversationId);

    uiStore.conversationUnreadCounts.delete(conversationId);
    uiStore.listOfMutedConversation.delete(conversationId);
    const totalUnreadCounts = uiStore.TotalUnreadMessageCountsComputed;
    uiStore.setTotalUnreadMessagesCount(totalUnreadCounts.unreadMessages);

    // Navigate away if this was the current conversation
    if (conversationStore.CurrentConversation?.id === conversationId) {
      void navigate('/chat');
    }

    // Track analytics event
    if (conversationType === 'one-to-one') {
      pushToGTMDataLayer('chat_1:1_delete', { conversationId });
    } else if (
      conversationType === 'named-group' ||
      conversationType === 'adhoc-group'
    ) {
      if (isActiveGroup) {
        pushToGTMDataLayer('chat_group_leave_and_delete', { conversationId });
      } else {
        pushToGTMDataLayer('chat_inactive_group_delete', { conversationId });
      }
    }

    // Show success notification
    useNotificationStore.getState().addNotify({
      type: 'success',
      dismissible: true,
      autoDismiss: true,
      title: 'Success',
      message: 'Conversation deleted.',
    });

    return deleteConversationResponse;
  } catch (error) {
    // Show error notification with retry option
    const notificationId = useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error',
      message: "The conversation couldn't be deleted.",
      action: {
        label: 'Retry',
        onClick: () => {
          // Pass the current notification ID to remove it on retry
          void deleteConversation(
            rootStore,
            navigate,
            conversationId,
            conversationType,
            isActiveGroup,
            notificationId
          );
        },
      },
    });

    throw error;
  } finally {
    conversationStore.unmarkConversationAsDeleting(conversationId);
  }
};

export const useDeleteConversation = () => {
  const navigate = useNavigate();
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  return useMutation({
    mutationKey: ['deleteConversation'],
    mutationFn: async ({
      conversationId,
      conversationType,
      isActiveGroup,
    }: {
      conversationId: string;
      conversationType: ConversationType;
      isActiveGroup?: boolean;
    }) => {
      const deletedConversation = await deleteConversation(
        rootStore,
        navigate,
        conversationId,
        conversationType,
        isActiveGroup
      );

      return deletedConversation;
    },
  });
};

export const markConversationAsRead = async (
  rootStore: RootStoreProps,
  conversationId: string,
  source: 'list' | 'conversation' = 'list'
) => {
  const { person: personStore } = rootStore;

  try {
    await personStore.waitUntilLoggedIn();

    const response = await API.post<{ code?: string }>(
      API_ENDPOINTS.ConversationMarkAsRead(conversationId)
    );

    if (response?.data?.code === 'NO_ELIGIBLE_MESSAGES') {
      // Track analytics for this edge case
      pushToGTMDataLayer('chat_fail_mark_read', {
        source,
        conversationId,
        timestamp: new Date().toISOString(),
      });

      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: "Couldn't mark as read",
        message:
          'This conversation has no messages that can be marked as read.',
      });

      return { success: false };
    }

    // Analytics tracking
    pushToGTMDataLayer('chat_mark_read', {
      source,
      conversationId,
      timestamp: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error marking conversation as read',
      message: "We couldn't mark the conversation as read. Please try again.",
    });

    throw error;
  }
};

export const markConversationAsUnread = async (
  rootStore: RootStoreProps,
  conversationId: string,
  source: 'list' | 'conversation' = 'list'
) => {
  const { person: personStore } = rootStore;

  try {
    await personStore.waitUntilLoggedIn();

    const response = await API.post<{ code?: string }>(
      API_ENDPOINTS.ConversationMarkAsUnread(conversationId)
    );

    if (response?.data?.code === 'NO_ELIGIBLE_MESSAGES') {
      pushToGTMDataLayer('chat_fail_mark_unread', {
        source,
        conversationId,
        timestamp: new Date().toISOString(),
      });

      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: "Couldn't mark as unread",
        message:
          'This conversation has no messages that can be marked as unread.',
      });

      return { success: false };
    }

    // Analytics tracking
    pushToGTMDataLayer('chat_mark_unread', {
      source,
      conversationId,
      timestamp: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error marking conversation as unread',
      message: "We couldn't mark the conversation as unread. Please try again.",
    });

    throw error;
  }
};

export const useMarkConversationAsRead = () => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  return useMutation({
    mutationKey: [MUTATION_KEY_MARK_AS_READ],
    mutationFn: async ({
      conversationId,
      source,
    }: {
      conversationId: string;
      source?: 'list' | 'conversation';
    }) => {
      const result = await markConversationAsRead(
        rootStore,
        conversationId,
        source
      );

      return result;
    },
  });
};

export const useMarkConversationAsUnread = () => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);

  return useMutation({
    mutationKey: [MUTATION_KEY_MARK_AS_UNREAD],
    mutationFn: async ({
      conversationId,
      source,
    }: {
      conversationId: string;
      source?: 'list' | 'conversation';
    }) => {
      const result = await markConversationAsUnread(
        rootStore,
        conversationId,
        source
      );

      return result;
    },
  });
};
